import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from '../components/PageHeader';
import { Leaf, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const seasonColors = {
    spring: 'bg-green-100 text-green-700',
    summer: 'bg-yellow-100 text-yellow-700',
    autumn: 'bg-orange-100 text-orange-700',
    winter: 'bg-blue-100 text-blue-700',
    all_year: 'bg-primary/10 text-primary',
    // Also support capitalized versions
    Spring: 'bg-green-100 text-green-700',
    Summer: 'bg-yellow-100 text-yellow-700',
    Autumn: 'bg-orange-100 text-orange-700',
    Winter: 'bg-blue-100 text-blue-700',
    'All Year': 'bg-primary/10 text-primary',
};

const seasonLabels = {
    spring: 'Spring',
    summer: 'Summer',
    autumn: 'Autumn',
    winter: 'Winter',
    all_year: 'All Year',
};

export function TipsPage() {
    const navigate = useNavigate();
    const [tips, setTips] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTips();
    }, []);

    const fetchTips = async () => {
        try {
            const response = await axios.get(`${API}/tips`);
            setTips(response.data);
        } catch (error) {
            console.error('Error fetching tips:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container" data-testid="tips-page">
            <PageHeader title="Garden Tips" showBack />
            
            <div className="px-4 py-4">
                {/* Header */}
                <div className="card-base bg-primary/5 mb-6">
                    <div className="flex items-center gap-3">
                        <Leaf className="w-8 h-8 text-primary" />
                        <div>
                            <h2 className="font-bold text-foreground">Seasonal Advice</h2>
                            <p className="text-muted-foreground text-sm">Helpful tips from our gardening experts</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="tips-list">
                        {tips.length === 0 ? (
                            <div className="card-base text-center py-8">
                                <p className="text-muted-foreground">No tips available at the moment.</p>
                            </div>
                        ) : (
                            tips.map(tip => {
                                const seasonKey = tip.season || tip.season_tag || 'all_year';
                                const content = tip.description || tip.content || '';
                                return (
                                <button
                                    key={tip.id}
                                    onClick={() => navigate(`/tips/${tip.id}`)}
                                    className="w-full card-interactive text-left"
                                    data-testid={`tip-card-${tip.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                                                seasonColors[seasonKey] || seasonColors['all_year']
                                            }`}>
                                                {seasonLabels[seasonKey] || seasonKey}
                                            </span>
                                            <h3 className="text-lg font-bold text-foreground">{tip.title}</h3>
                                            <p className="text-muted-foreground line-clamp-2 mt-1">
                                                {content.split('\n')[0]}
                                            </p>
                                        </div>
                                        <ChevronRight className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-2" />
                                    </div>
                                </button>
                            );})
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TipsPage;
